import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { API_URL } from "../../config";
import {
  APP_NAME,
  EXPERIENCES,
  createStudyCompanionConfig,
} from "../../config/earthDayExperience";
import "./study-companion.scss";
import { SchemaType } from "@google/generative-ai";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

interface Citation {
  source: string;
  page: number;
  excerpt?: string;
}

interface LibraryFile {
  file: File;
  name: string;
  id: string;
  source: "backend" | "local";
  indexed: boolean;
  size: number;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
}

interface DragPosition {
  x: number;
  y: number;
}

interface UploadFeedback {
  kind: "idle" | "uploading" | "success" | "error";
  title: string;
  detail?: string;
}

type NavigationIntent =
  | { kind: "none" }
  | { kind: "page"; page: number; explain: boolean }
  | { kind: "topic"; topic: string };

type RagResult = {
  text: string;
  source: string;
  page_num: number;
  relevance_score?: number | null;
};

type PreparedPdf = {
  blob: Blob;
  objectUrl: string;
  totalPages: number;
  pageText?: string;
};

type CachedPdf = {
  blob: Blob;
  objectUrl: string;
  totalPages: number;
  pageTextByPage: Record<number, string>;
  pdfDocument: any;
};

const FLOATING_PANEL_TOP_OFFSET = 118;

const normalizePdfName = (value: string) =>
  value.toLowerCase().replace(/\.pdf$/i, "").trim();

const truncateText = (value: string, maxLength = 280) => {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}...`;
};

const extractNavigationIntent = (input: string): NavigationIntent => {
  const trimmed = input.trim();
  if (!trimmed) return { kind: "none" };

  const pageMatch = trimmed.match(/\b(?:page|pg\.?)\s*(\d{1,4})\b/i);
  const hasNavigationVerb = /\b(?:go to|take me to|open|show me|bring me to|jump to|navigate to)\b/i.test(
    trimmed,
  );
  const wantsExplanation =
    /\b(?:explain|summari[sz]e|tell me about|talk me through|walk me through|what(?:'s| is) (?:there|on))\b/i.test(
      trimmed,
    );

  if (
    pageMatch &&
    (hasNavigationVerb || wantsExplanation || /\bon page\b/i.test(trimmed))
  ) {
    return {
      kind: "page",
      page: Number(pageMatch[1]),
      explain: wantsExplanation,
    };
  }

  const topicPatterns = [
    /\bshow me the part about\s+(.+?)$/i,
    /\btake me to the page where\s+(.+?)\s+(?:is|are)\s+(?:discussed|mentioned|explained|covered)\b/i,
    /\b(?:take me to|open|go to|show me|bring me to|jump to)\s+the\s+(.+?)\s+(?:section|part|page|passage)\b/i,
    /\bwhere (?:does|is)\s+(.+?)\s+(?:discussed|mentioned|explained|covered)\b/i,
  ];

  for (const pattern of topicPatterns) {
    const match = trimmed.match(pattern);
    if (match?.[1]) {
      const topic = match[1].replace(/[?.!]+$/, "").trim();
      if (topic) {
        return { kind: "topic", topic };
      }
    }
  }

  return { kind: "none" };
};

const formatFileSize = (size: number) => {
  if (!size) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export const StudyCompanion: React.FC = () => {
  const { client, connected, setConfig } = useLiveAPIContext();
  const [indexedFiles, setIndexedFiles] = useState<LibraryFile[]>([]);
  const [pendingUploads, setPendingUploads] = useState<LibraryFile[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [panelPosition, setPanelPosition] = useState<DragPosition>({
    x: 20,
    y: FLOATING_PANEL_TOP_OFFSET,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<DragPosition>({ x: 0, y: 0 });
  const [highlightText, setHighlightText] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [uploadFeedback, setUploadFeedback] = useState<UploadFeedback>({
    kind: "idle",
    title: "",
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfCacheRef = useRef<Map<string, CachedPdf>>(new Map());
  const mainScrollTopRef = useRef(0);

  const libraryFiles = useMemo(
    () => [...pendingUploads, ...indexedFiles],
    [indexedFiles, pendingUploads],
  );

  const chromaDBTool = {
    name: "fetch_study_context",
    description:
      "Fetch relevant climate-document context from the uploaded Earth Day library. Returns excerpts with page numbers and source PDF names.",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        query: {
          type: SchemaType.STRING,
          description: "The climate or sustainability question to search for",
        },
        top_k: {
          type: SchemaType.NUMBER,
          description: "Number of top results to return (default: 5)",
        },
      },
      required: ["query"],
    },
  };

  const highlightPDFTool = {
    name: "highlight_pdf_content",
    description:
      "Open a PDF to a specific page and optionally highlight a matching excerpt in the viewer.",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        source_pdf: {
          type: SchemaType.STRING,
          description: "The name of the PDF file to highlight in",
        },
        page_number: {
          type: SchemaType.NUMBER,
          description: "The page number to navigate to",
        },
        text_to_highlight: {
          type: SchemaType.STRING,
          description: "The specific text excerpt to highlight",
        },
      },
      required: ["source_pdf", "page_number"],
    },
  };

  const refreshIndexedFiles = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/rag/files`);
      if (!response.ok) {
        throw new Error(`Library request failed with status ${response.status}`);
      }

      const data = await response.json();
      const backendFiles = (data.files || []).map((file: any) => ({
        file: new File([], file.name, { type: "application/pdf" }),
        name: file.name,
        id: file.name,
        source: "backend" as const,
        indexed: true,
        size: file.size || 0,
      }));

      setIndexedFiles(backendFiles);
    } catch (error) {
      console.error("Error loading indexed library:", error);
      setUploadFeedback({
        kind: "error",
        title: "Could not load the indexed library.",
        detail: "The backend file list is unavailable right now.",
      });
    }
  }, []);

  const appendAssistantMessage = useCallback(
    (content: string, citations?: Citation[]) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content,
          citations,
        },
      ]);
    },
    [],
  );

  const getPdfCacheKey = useCallback((file: File) => {
    return normalizePdfName(file.name);
  }, []);

  const getPdfBlob = useCallback(async (file: File) => {
    if (file.size === 0 && file.name) {
      const response = await fetch(
        `${API_URL}/api/rag/download/${encodeURIComponent(file.name)}`,
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to download PDF (${response.status}): ${errorText || file.name}`,
        );
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Downloaded PDF file is empty");
      }

      return new Blob([blob], { type: "application/pdf" });
    }

    if (file.size > 0) {
      return new Blob([file], { type: "application/pdf" });
    }

    throw new Error("Invalid PDF file");
  }, []);

  const preparePdf = useCallback(
    async (file: File, pageNumber = 1, extractPageText = false) => {
      const cacheKey = getPdfCacheKey(file);
      let cachedPdf = pdfCacheRef.current.get(cacheKey);

      if (!cachedPdf) {
        const blob = await getPdfBlob(file);
        const buffer = await blob.arrayBuffer();
        const loadingTask = pdfjs.getDocument({ data: new Uint8Array(buffer) });
        const pdfDocument = await loadingTask.promise;

        cachedPdf = {
          blob,
          objectUrl: URL.createObjectURL(blob),
          totalPages: pdfDocument.numPages,
          pageTextByPage: {},
          pdfDocument,
        };

        pdfCacheRef.current.set(cacheKey, cachedPdf);
      }

      let pageText: string | undefined;
      if (
        extractPageText &&
        pageNumber >= 1 &&
        pageNumber <= cachedPdf.totalPages
      ) {
        pageText = cachedPdf.pageTextByPage[pageNumber];

        if (!pageText) {
          const page = await cachedPdf.pdfDocument.getPage(pageNumber);
          const textContent = await page.getTextContent();
          const extractedText = textContent.items
            .map((item: any) => ("str" in item ? item.str : ""))
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();
          pageText = extractedText;
          cachedPdf.pageTextByPage[pageNumber] = extractedText;
        }
      }

      return {
        blob: cachedPdf.blob,
        objectUrl: cachedPdf.objectUrl,
        totalPages: cachedPdf.totalPages,
        pageText,
      } as PreparedPdf;
    },
    [getPdfBlob, getPdfCacheKey],
  );

  const applyPdfView = useCallback(
    (file: File, preparedPdf: PreparedPdf, pageNumber = 1) => {
      setPdfError(null);
      setPdfUrl(preparedPdf.objectUrl);
      setSelectedPdf(file);
      setNumPages(preparedPdf.totalPages);
      setCurrentPage(pageNumber);
    },
    [],
  );

  const handleOpenPDF = async (file: File, pageNumber?: number) => {
    try {
      const targetPage = pageNumber || 1;
      const preparedPdf = await preparePdf(file, targetPage, false);
      const safePage = Math.min(
        Math.max(targetPage, 1),
        Math.max(preparedPdf.totalPages, 1),
      );
      applyPdfView(file, preparedPdf, safePage);
    } catch (error) {
      console.error("Error opening PDF:", error);
      setPdfError(`Error opening PDF: ${error}`);
    }
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (isDragging && panelRef.current) {
      const newX = event.clientX - dragOffset.x;
      const newY = event.clientY - dragOffset.y;

      const maxX = window.innerWidth - panelRef.current.offsetWidth;
      const maxY = window.innerHeight - panelRef.current.offsetHeight;

      setPanelPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(FLOATING_PANEL_TOP_OFFSET, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragOffset]);

  useEffect(() => {
    if (pdfUrl && panelPosition.y < FLOATING_PANEL_TOP_OFFSET) {
      setPanelPosition((prev) => ({
        ...prev,
        y: FLOATING_PANEL_TOP_OFFSET,
      }));
    }
  }, [panelPosition.y, pdfUrl]);

  const goToPage = (page: number) => {
    if (numPages > 0) {
      setCurrentPage(Math.min(Math.max(page, 1), numPages));
      return;
    }

    setCurrentPage(Math.max(page, 1));
  };

  const applyHighlight = useCallback(() => {
    if (!highlightText) return;
    document.querySelectorAll(".pdf-text-highlight").forEach((element) => {
      (element as HTMLElement).style.backgroundColor = "";
      element.classList.remove("pdf-text-highlight");
    });
    document
      .querySelectorAll(".react-pdf__Page__textContent span")
      .forEach((span) => {
        const text = span.textContent?.trim() || "";
        if (
          text.length > 2 &&
          highlightText.toLowerCase().includes(text.toLowerCase())
        ) {
          (span as HTMLElement).style.backgroundColor =
            "rgba(255, 220, 0, 0.65)";
          span.classList.add("pdf-text-highlight");
        }
      });
  }, [highlightText]);

  useEffect(() => {
    refreshIndexedFiles();
  }, [refreshIndexedFiles]);

  useEffect(() => {
    setConfig(
      createStudyCompanionConfig(libraryFiles.map((file) => file.name), [
        {
          googleSearch: {},
        },
        {
          functionDeclarations: [chromaDBTool, highlightPDFTool],
        },
      ]),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConfig, libraryFiles]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    const handleToolCall = async (toolCall: any) => {
      const functionCalls = toolCall.functionCalls || [];

      for (const call of functionCalls) {
        const toolName = call.name;
        const toolInput = call.args || {};
        const callId = call.id;

        if (toolName === "fetch_study_context") {
          try {
            const response = await fetch(`${API_URL}/api/rag/query`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                query: toolInput.query,
                top_k: toolInput.top_k || 10,
              }),
            });

            if (response.ok) {
              const data = await response.json();
              const contextResults = data.results.map((result: any) => ({
                text: result.text,
                source: result.source,
                page_num: result.page_num,
                relevance_score: result.relevance_score,
              }));

              client.sendToolResponse({
                functionResponses: [
                  {
                    id: callId,
                    response: {
                      success: true,
                      results: contextResults,
                      total_results: contextResults.length,
                    },
                  },
                ],
              });

              sessionStorage.setItem(
                "fetchedContext",
                JSON.stringify(data.results),
              );
            }
          } catch (error) {
            console.error("Tool fetch error:", error);
            client.sendToolResponse({
              functionResponses: [
                {
                  id: callId,
                  response: {
                    success: false,
                    error: String(error),
                  },
                },
              ],
            });
          }
        } else if (toolName === "highlight_pdf_content") {
          try {
            const targetPdf = libraryFiles.find((file) =>
              file.name
                .toLowerCase()
                .includes(toolInput.source_pdf.toLowerCase()),
            );

            if (targetPdf) {
              handleOpenPDF(targetPdf.file, toolInput.page_number);
              setHighlightText(toolInput.text_to_highlight || null);

              client.sendToolResponse({
                functionResponses: [
                  {
                    id: callId,
                    response: {
                      success: true,
                      message: `Opened ${toolInput.source_pdf} at page ${toolInput.page_number}`,
                    },
                  },
                ],
              });
            } else {
              client.sendToolResponse({
                functionResponses: [
                  {
                    id: callId,
                    response: {
                      success: false,
                      error: `PDF "${toolInput.source_pdf}" not found in the climate library`,
                    },
                  },
                ],
              });
            }
          } catch (error) {
            console.error("Highlight tool error:", error);
            client.sendToolResponse({
              functionResponses: [
                {
                  id: callId,
                  response: {
                    success: false,
                    error: String(error),
                  },
                },
              ],
            });
          }
        }
      }
    };

    if (client && connected) {
      client.on("toolcall", handleToolCall);
    }

    return () => {
      client.off("toolcall", handleToolCall);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, connected, libraryFiles]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const existingNames = new Set(
      libraryFiles.map((file) => file.name.toLowerCase()),
    );
    const acceptedFiles: LibraryFile[] = [];
    const skippedFiles: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.type !== "application/pdf") {
        skippedFiles.push(`${file.name} is not a PDF.`);
        return;
      }

      if (existingNames.has(file.name.toLowerCase())) {
        skippedFiles.push(
          `${file.name} is already indexed or already pending upload.`,
        );
        return;
      }

      acceptedFiles.push({
        file,
        name: file.name,
        id: Math.random().toString(36).slice(2, 11),
        source: "local",
        indexed: false,
        size: file.size,
      });
      existingNames.add(file.name.toLowerCase());
    });

    if (acceptedFiles.length > 0) {
      setPendingUploads((prev) => [...prev, ...acceptedFiles]);
      setUploadFeedback({
        kind: "success",
        title: `${acceptedFiles.length} PDF${
          acceptedFiles.length > 1 ? "s are" : " is"
        } ready to add.`,
        detail:
          "Click Add to climate library to save the files locally and prepare semantic retrieval.",
      });
    }

    if (skippedFiles.length > 0) {
      setUploadFeedback({
        kind: "error",
        title: "Some files were skipped.",
        detail: skippedFiles.join(" "),
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (id: string) => {
    const removedPending = pendingUploads.find((file) => file.id === id);
    setPendingUploads((prev) => prev.filter((file) => file.id !== id));

    if (removedPending?.file === selectedPdf) {
      setPdfUrl(null);
      setSelectedPdf(null);
    }

    if (pendingUploads.length <= 1) {
      setUploadFeedback({ kind: "idle", title: "" });
    }
  };

  const uploadPDFsToBackend = async () => {
    if (pendingUploads.length === 0) {
      setUploadFeedback({
        kind: "error",
        title: "No pending PDFs to add.",
        detail: "Select one or more local PDFs before starting ingestion.",
      });
      return;
    }

    const formData = new FormData();
    pendingUploads.forEach((file) => {
      formData.append("files", file.file);
    });

    try {
      setUploadFeedback({
        kind: "uploading",
        title: "Uploading and preparing semantic search...",
        detail:
          "The PDFs are being saved to the backend, chunked, and embedded into the local ChromaDB index. Small PDFs can still take a few seconds.",
      });

      const response = await fetch(`${API_URL}/api/rag/upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        let message = "Upload failed";
        try {
          const errorData = await response.json();
          message = errorData.message || message;
        } catch {
          // Keep the fallback message if the response is not JSON.
        }
        throw new Error(message);
      }

      const data = await response.json();
      const uploadCount = data.uploaded_count || pendingUploads.length;
      const chunkCount = data.ingest_result?.total_chunks_added;

      setPendingUploads([]);
      await refreshIndexedFiles();
      setUploadFeedback({
        kind: "success",
        title: `Added ${uploadCount} PDF${
          uploadCount > 1 ? "s" : ""
        } to the climate library.`,
        detail:
          chunkCount != null
            ? `Saved in backend/uploaded_pdfs and indexed ${chunkCount} searchable chunks in backend/chroma_db.`
            : "The files were saved locally and added to the semantic search index.",
      });
    } catch (error) {
      console.error("Upload error:", error);
      setUploadFeedback({
        kind: "error",
        title: "Climate library upload failed.",
        detail:
          error instanceof Error
            ? error.message
            : "The backend could not save or index the selected PDFs.",
      });
    }
  };

  const findLibraryFile = useCallback(
    (sourceName: string) =>
      libraryFiles.find((entry) => {
        const entryName = normalizePdfName(entry.name);
        const source = normalizePdfName(sourceName);
        return (
          entryName === source ||
          entryName.includes(source) ||
          source.includes(entryName)
        );
      }),
    [libraryFiles],
  );

  const resolveTargetPdfForPageNavigation = useCallback(() => {
    if (selectedPdf) {
      return (
        libraryFiles.find(
          (entry) =>
            normalizePdfName(entry.name) === normalizePdfName(selectedPdf.name),
        ) || {
          file: selectedPdf,
          name: selectedPdf.name,
          id: selectedPdf.name,
          source: "local" as const,
          indexed: selectedPdf.size === 0,
          size: selectedPdf.size,
        }
      );
    }

    if (indexedFiles.length === 1) {
      return indexedFiles[0];
    }

    return null;
  }, [indexedFiles, libraryFiles, selectedPdf]);

  const fetchRagResults = useCallback(async (query: string, topK = 5) => {
    const response = await fetch(`${API_URL}/api/rag/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query,
        top_k: topK,
      }),
    });

    if (!response.ok) {
      throw new Error("Query failed");
    }

    const data = await response.json();
    return (data.results || []) as RagResult[];
  }, []);

  const resolveHighlightExcerpt = useCallback(
    (source: string, page: number, excerpt?: string) => {
      if (excerpt?.trim()) {
        return excerpt;
      }

      try {
        const storedResults = JSON.parse(
          sessionStorage.getItem("ragCitations") || "[]",
        ) as RagResult[];

        const match = storedResults.find(
          (result) =>
            normalizePdfName(result.source) === normalizePdfName(source) &&
            result.page_num === page &&
            result.text?.trim(),
        );

        return match?.text || null;
      } catch (error) {
        console.error("Error resolving highlight excerpt:", error);
        return null;
      }
    },
    [],
  );

  const requestGeminiNarration = useCallback(
    async (
      prompt: string,
      citations: Citation[],
      fallbackContent: string,
    ) => {
      if (!client || !connected) {
        appendAssistantMessage(fallbackContent, citations);
        return;
      }

      await new Promise<void>((resolve) => {
        let assistantResponse = "";

        const handleContent = (content: any) => {
          if (content.parts) {
            content.parts.forEach((part: any) => {
              if (part.text) {
                assistantResponse += part.text;
              }
            });
          }
        };

        const handleTurnComplete = () => {
          client.off("content", handleContent);
          client.off("turncomplete", handleTurnComplete);
          appendAssistantMessage(
            assistantResponse.trim() || fallbackContent,
            citations,
          );
          resolve();
        };

        client.on("content", handleContent);
        client.on("turncomplete", handleTurnComplete);

        try {
          client.send({
            text: prompt,
          });
        } catch (error) {
          console.error("Gemini narration error:", error);
          client.off("content", handleContent);
          client.off("turncomplete", handleTurnComplete);
          appendAssistantMessage(fallbackContent, citations);
          resolve();
        }
      });
    },
    [appendAssistantMessage, client, connected],
  );

  const handlePageNavigation = useCallback(
    async (questionText: string, page: number, explain: boolean) => {
      const targetFile = resolveTargetPdfForPageNavigation();

      if (!targetFile) {
        appendAssistantMessage(
          indexedFiles.length === 0
            ? "Upload and index a PDF first, then I can open exact pages for you."
            : "I can open that page, but I need to know which PDF to use. Open a document first or narrow it to one file in the library.",
        );
        return;
      }

      try {
        setHighlightText(null);
        const preparedPdf = await preparePdf(targetFile.file, page, explain);

        if (page < 1 || page > preparedPdf.totalPages) {
          appendAssistantMessage(
            `"${targetFile.name}" has ${preparedPdf.totalPages} page${
              preparedPdf.totalPages === 1 ? "" : "s"
            }. I kept your current view unchanged.`,
          );
          return;
        }

        applyPdfView(targetFile.file, preparedPdf, page);

        const citations = [
          {
            source: targetFile.name,
            page,
            excerpt: preparedPdf.pageText,
          },
        ];
        if (!explain) {
          appendAssistantMessage(
            `Opened page ${page} of ${targetFile.name}.`,
            citations,
          );
          return;
        }

        const pageText = preparedPdf.pageText || "";
        const fallbackContent = pageText
          ? `Opened page ${page} of ${targetFile.name}. This page covers: ${truncateText(
              pageText,
              280,
            )}`
          : `Opened page ${page} of ${targetFile.name}.`;

        await requestGeminiNarration(
          `You are the ${APP_NAME} climate library guide.

The user asked: "${questionText}"
You already opened ${targetFile.name} to page ${page}.

Using only the page text below, explain what is on this page in 3 to 5 concise sentences. Mention that the page is open in the viewer. Do not call tools. Do not invent anything that is not present in the page text.

Page text:
${pageText || "No page text could be extracted."}`,
          citations,
          fallbackContent,
        );
      } catch (error) {
        console.error("Page navigation error:", error);
        appendAssistantMessage(
          "I could not open that page right now. The PDF may be unavailable or still loading from the local library.",
        );
      }
    },
    [
      appendAssistantMessage,
      applyPdfView,
      indexedFiles.length,
      preparePdf,
      requestGeminiNarration,
      resolveTargetPdfForPageNavigation,
    ],
  );

  const handleTopicNavigation = useCallback(
    async (questionText: string, topic: string) => {
      try {
        const results = await fetchRagResults(topic, 5);
        const topResult = results[0];

        const isConfidentMatch =
          Boolean(topResult?.source) &&
          Boolean(topResult?.page_num) &&
          (topResult.relevance_score == null ||
            topResult.relevance_score >= 0.42);

        if (!isConfidentMatch || !topResult) {
          appendAssistantMessage(
            `I found possible references for "${topic}", but not a confident enough match to jump automatically. Try a clearer phrase or name the document.`,
          );
          return;
        }

        const targetFile = findLibraryFile(topResult.source);
        if (!targetFile) {
          appendAssistantMessage(
            `I found a likely match in ${topResult.source}, but that PDF is not available in the current viewer list.`,
          );
          return;
        }

        const preparedPdf = await preparePdf(
          targetFile.file,
          topResult.page_num,
          false,
        );

        if (topResult.page_num < 1 || topResult.page_num > preparedPdf.totalPages) {
          appendAssistantMessage(
            `I found a relevant section in ${topResult.source}, but its cited page is outside the current page range.`,
          );
          return;
        }

        applyPdfView(targetFile.file, preparedPdf, topResult.page_num);
        setHighlightText(topResult.text || null);

        const citations = [
          {
            source: topResult.source,
            page: topResult.page_num,
            excerpt: topResult.text,
          },
        ];

        const fallbackContent = `Opened ${topResult.source} to page ${topResult.page_num}. This section looks relevant to "${topic}": ${truncateText(
          topResult.text || "",
          260,
        )}`;

        await requestGeminiNarration(
          `You are the ${APP_NAME} climate library guide.

The user asked: "${questionText}"
You already opened ${topResult.source} to page ${topResult.page_num} and highlighted the most relevant excerpt.

Explain what this section is about in 3 to 5 concise sentences using only the excerpt below. Mention that the page is open in the viewer. Do not call tools. Stay grounded in the excerpt.

Excerpt:
${topResult.text}`,
          citations,
          fallbackContent,
        );
      } catch (error) {
        console.error("Topic navigation error:", error);
        appendAssistantMessage(
          "I could not search the climate library for that section right now.",
        );
      }
    },
    [
      appendAssistantMessage,
      applyPdfView,
      fetchRagResults,
      findLibraryFile,
      preparePdf,
      requestGeminiNarration,
    ],
  );

  const handleSendMessage = async () => {
    if (!question.trim() || isLoading) return;

    const userMessage: Message = {
      role: "user",
      content: question,
    };

    setMessages((prev) => [...prev, userMessage]);
    setQuestion("");
    setIsLoading(true);

    try {
      const navigationIntent = extractNavigationIntent(question);

      if (navigationIntent.kind === "page") {
        await handlePageNavigation(
          question,
          navigationIntent.page,
          navigationIntent.explain,
        );
        setIsLoading(false);
        return;
      }

      if (navigationIntent.kind === "topic") {
        await handleTopicNavigation(question, navigationIntent.topic);
        setIsLoading(false);
        return;
      }

      if (!connected) {
        appendAssistantMessage(
          "Start the Gemini live session to ask document questions. Direct page jumps still work once the session is active.",
        );
        setIsLoading(false);
        return;
      }

      const results = await fetchRagResults(question, 5);
      const ragContext = results
        .map(
          (result) =>
            `Source: ${result.source}, Page: ${result.page_num}\nContent: ${result.text}`,
        )
        .join("\n\n");

      sessionStorage.setItem("ragCitations", JSON.stringify(results));

      const systemPrompt = `You are the ${APP_NAME} climate library guide. Answer the question using the provided document context first. If the document context does not contain the answer, say that clearly and suggest a focused follow-up angle.

Climate Reference Context:
${ragContext}

User Question: ${question}`;

      if (client && connected) {
        client.send({
          text: systemPrompt,
        });

        let assistantResponse = "";
        const handleContent = (content: any) => {
          if (content.parts) {
            content.parts.forEach((part: any) => {
              if (part.text) {
                assistantResponse += part.text;
              }
            });
          }
        };

        const handleTurnComplete = () => {
          const citations = JSON.parse(
            sessionStorage.getItem("ragCitations") || "[]",
          );

          const assistantMessage: Message = {
            role: "assistant",
            content: assistantResponse,
            citations: citations.map((citation: any) => ({
              source: citation.source,
              page: citation.page_num,
              excerpt: citation.text,
            })),
          };

          setMessages((prev) => [...prev, assistantMessage]);
          client.off("content", handleContent);
          client.off("turncomplete", handleTurnComplete);
          setIsLoading(false);
        };

        client.on("content", handleContent);
        client.on("turncomplete", handleTurnComplete);
      }
    } catch (error) {
      console.error("Query error:", error);
      alert("Failed to query the climate library.");
      setIsLoading(false);
    }
  };

  const handleCitationClick = async (
    source: string,
    page: number,
    excerpt?: string,
  ) => {
    const file = findLibraryFile(source);
    if (file) {
      const nextHighlight = resolveHighlightExcerpt(source, page, excerpt);
      setHighlightText(nextHighlight);
      await handleOpenPDF(file.file, page);
    }
  };

  const closePDF = () => {
    setPdfUrl(null);
    setSelectedPdf(null);
    setCurrentPage(1);
    setHighlightText(null);
    setPdfError(null);
  };

  useEffect(() => {
    const pdfCache = pdfCacheRef.current;

    return () => {
      pdfCache.forEach((cachedPdf) => {
        try {
          URL.revokeObjectURL(cachedPdf.objectUrl);
        } catch (error) {
          console.error("Error revoking cached PDF URL on unmount:", error);
        }

        if (typeof cachedPdf.pdfDocument?.destroy === "function") {
          cachedPdf.pdfDocument.destroy().catch((error: unknown) => {
            console.error("Error destroying cached PDF document:", error);
          });
        }
      });
      pdfCache.clear();
    };
  }, []);

  useEffect(() => {
    if (!pdfUrl || window.innerWidth <= 960) {
      return;
    }

    const mainElement = containerRef.current?.closest("main");
    mainScrollTopRef.current =
      mainElement instanceof HTMLElement ? mainElement.scrollTop : 0;
    const previousMainOverflow =
      mainElement instanceof HTMLElement ? mainElement.style.overflow : "";
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    if (mainElement instanceof HTMLElement) {
      mainElement.scrollTo({ top: 0, behavior: "auto" });
      mainElement.style.overflow = "hidden";
    }
    window.scrollTo({ top: 0, behavior: "auto" });
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      if (mainElement instanceof HTMLElement) {
        mainElement.style.overflow = previousMainOverflow;
        mainElement.scrollTo({
          top: mainScrollTopRef.current,
          behavior: "auto",
        });
      }
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [pdfUrl]);

  return (
    <div ref={containerRef} className="study-companion-container">
      {!pdfUrl ? (
        <div className="upload-view">
          <div className="study-companion-header">
            <span className="section-label">{EXPERIENCES.study.eyebrow}</span>
            <h1>{EXPERIENCES.study.title}</h1>
            <p>
              Build a grounded Earth Day library from reports, toolkits, and
              sustainability PDFs, then ask source-aware questions.
            </p>
          </div>

          <div className="upload-section-center">
            <div className="upload-section-copy">
              <h3>Your climate references</h3>
              <p>
                Upload Earth Day resources, climate policy briefs, biodiversity
                explainers, or local sustainability reports.
              </p>
            </div>

            <div className="library-explainer">
              <p>
                Uploads are stored locally in <code>backend/uploaded_pdfs</code>,
                then chunked and embedded into <code>backend/chroma_db</code> so
                Gemini answers from retrieved document context rather than
                reparsing the full PDF on every question.
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              onChange={handleFileUpload}
              className="file-input"
            />

            <div
              className="upload-box"
              onClick={() => fileInputRef.current?.click()}
            >
              <p className="upload-hint">
                Select PDF references for your library
              </p>
              <span className="upload-subhint">
                Examples: teach-in guides, climate reports, circular economy
                explainers
              </span>
            </div>

            {libraryFiles.length > 0 && (
              <div className="file-list">
                <h4>Library items ({libraryFiles.length})</h4>
                <ul>
                  {libraryFiles.map((file) => (
                    <li key={file.id}>
                      <div className="file-meta">
                        <span className="file-name">{file.name}</span>
                        <div className="file-badges">
                          <span
                            className={`file-badge ${
                              file.indexed ? "indexed" : "pending"
                            }`}
                          >
                            {file.indexed ? "Indexed" : "Pending upload"}
                          </span>
                          {formatFileSize(file.size) && (
                            <span className="file-size">
                              {formatFileSize(file.size)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="file-actions">
                        <button
                          className="view-btn"
                          onClick={() => handleOpenPDF(file.file)}
                        >
                          📖 Open
                        </button>
                        {!file.indexed && (
                          <button
                            className="remove-btn"
                            onClick={() => handleRemoveFile(file.id)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={`upload-feedback ${uploadFeedback.kind}`}>
              {uploadFeedback.kind === "uploading" && (
                <span className="upload-spinner" aria-hidden="true" />
              )}
              <div className="upload-feedback-copy">
                <strong>
                  {uploadFeedback.kind === "idle"
                    ? "Select PDFs, then add them to the climate library."
                    : uploadFeedback.title}
                </strong>
                <span>
                  {uploadFeedback.kind === "idle"
                    ? "You will see the files as pending first, then they will be saved and indexed for semantic search."
                    : uploadFeedback.detail}
                </span>
              </div>
            </div>

            <div className="connection-buttons">
              <button
                className="connect-btn"
                onClick={uploadPDFsToBackend}
                disabled={
                  pendingUploads.length === 0 ||
                  uploadFeedback.kind === "uploading"
                }
              >
                {uploadFeedback.kind === "uploading"
                  ? "Saving and indexing..."
                  : "Add to climate library"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="pdf-viewer-fullscreen">
          <div className="pdf-display">
            <div className="pdf-controls">
              <button className="close-pdf-btn" onClick={closePDF}>
                Back to library
              </button>
              <h3 className="pdf-title">{selectedPdf?.name}</h3>
              <div className="pdf-navigation">
                <button
                  className="page-nav-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                >
                  ←
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(currentPage)}
                  onChange={(event) => {
                    const nextValue = event.target.value.replace(/\D/g, "");
                    if (!nextValue) return;
                    const page = parseInt(nextValue, 10);
                    if (page > 0) goToPage(page);
                  }}
                  className="page-input"
                  aria-label="Current page"
                />
                <button
                  className="page-nav-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={numPages > 0 && currentPage >= numPages}
                  aria-label="Next page"
                >
                  →
                </button>
                <span className="page-info">Go to page</span>
              </div>
            </div>

            <div className="pdf-content">
              {pdfError && (
                <div className="pdf-error">
                  <p>❌ {pdfError}</p>
                  <button onClick={closePDF}>Go Back</button>
                </div>
              )}

              {pdfUrl && !pdfError && (
                <div className="pdf-react-container">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages: totalPages }) =>
                      setNumPages(totalPages)
                    }
                  >
                    <Page
                      pageNumber={currentPage}
                      renderTextLayer={true}
                      renderAnnotationLayer={false}
                      onRenderTextLayerSuccess={applyHighlight}
                      width={Math.min(window.innerWidth * 0.6, 900)}
                    />
                  </Document>
                  {numPages > 0 && (
                    <p className="pdf-page-count">
                      Page {currentPage} of {numPages}
                    </p>
                  )}
                </div>
              )}

              {highlightText && (
                <div className="highlight-indicator">
                  📍 Highlighting: "{highlightText}"
                  <button
                    className="clear-highlight-btn"
                    onClick={() => setHighlightText(null)}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          <div
            ref={panelRef}
            className={`floating-panel ${panelMinimized ? "minimized" : ""} ${
              isDragging ? "dragging" : ""
            }`}
            style={{
              left: `${panelPosition.x}px`,
              top: `${panelPosition.y}px`,
            }}
          >
            <div className="panel-header" onMouseDown={handleMouseDown}>
              <div className="panel-title">
                <span className="drag-handle">⋮⋮</span>
                Library guide
              </div>
              <button
                className="minimize-toggle"
                onClick={() => setPanelMinimized(!panelMinimized)}
              >
                {panelMinimized ? "▼" : "▲"}
              </button>
            </div>

            {!panelMinimized && (
              <div className="panel-content">
                <div className="messages-container">
                  {messages.length === 0 && (
                    <div className="welcome-message">
                      <p>
                        Ask about evidence, summaries, tradeoffs, or action
                        ideas from this document.
                      </p>
                      <span className="welcome-note">
                        Answers are grounded in indexed chunks retrieved from
                        your library before Gemini responds.
                      </span>
                      <span className="welcome-note navigation-note">
                        Try: "Open page 3," "Take me to the latest trends
                        section," or "Show me the part about waste reduction."
                      </span>
                      <ul className="welcome-prompts">
                        <li>
                          What does this report say about renewable energy
                          access?
                        </li>
                        <li>Summarize the key risks in plain language.</li>
                        <li>
                          Show me the passage on community action or local
                          policy.
                        </li>
                      </ul>
                    </div>
                  )}

                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`message message-${message.role}`}
                    >
                      <div className="message-content">
                        <p>{message.content}</p>
                        {message.citations && message.citations.length > 0 && (
                          <div className="citations">
                            <strong>Sources</strong>
                            <ul>
                              {message.citations.map((citation, citationIndex) => (
                                <li key={citationIndex}>
                                  <button
                                    className="citation-btn"
                                    onClick={() =>
                                      handleCitationClick(
                                        citation.source,
                                        citation.page,
                                        citation.excerpt,
                                      )
                                    }
                                  >
                                    {citation.source} - Page {citation.page}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {isLoading && (
                    <div className="message message-assistant loading">
                      <div className="message-content">
                        <p>Searching the library and grounding the answer...</p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="input-section">
                  <textarea
                    className="question-input"
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    placeholder="Ask a question or say: Open page 3, show me the part about biodiversity, or explain the risks..."
                    disabled={isLoading}
                    onKeyPress={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    rows={2}
                  />
                  <button
                    className="send-btn"
                    onClick={handleSendMessage}
                    disabled={isLoading || !question.trim()}
                  >
                    Ask
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
